import { PortfolioExportRawSchema } from "@/tools/aggregate/portfolio-export/schema";
import { unparse } from "@climaterisk/papaparse";
import type { Polygon } from "leaflet";
import Router from "next/router";

export interface LargeSiteScreeningHandoffData {
  assets: PortfolioExportRawSchema[];
  polygons: Polygon[];
  /** Marks this handoff as tutorial-driven demo data, rather than a real upload. */
  isTutorial?: boolean;
}

/**
 * Send a collection of assets to a new run in the Large Site Screening tool.
 */
export const handoffToLargeSiteScreening = async (data: LargeSiteScreeningHandoffData) => {
  return handoff(
    "largeSiteScreenHandoffData",
    "/tools/comprehensive/large-site-analysis",
    JSON.stringify({
      assets: unparse(data.assets),
      polygons: data.polygons.map((polygon) => {
        const geoJson = polygon.toGeoJSON();
        geoJson.properties.name = polygon.options.attribution;
        return geoJson;
      }),
      ...(data.isTutorial && { isTutorial: true }),
    })
  );
};

/**
 * Send a collection of assets to a new run in the Portfolio Export tool.
 */
export const handoffToPortfolioExport = async (assets: PortfolioExportRawSchema[]) => {
  return handoff("portfolioExportHandoffData", "/tools/comprehensive/portfolio-analysis", unparse(assets));
};

/**
 * Send a collection of assets to a new run in the Portfolio Export tool.
 */
const handoff = async (localStorageKey: string, route: string, data: string) => {
  // TODO: Check for limits of portfolio size, taking large bank status into account.

  // We are using session storage since the 5MiB limit is fairly consistent,
  // but if we run into issues, indexedDB is there.
  try {
    sessionStorage.setItem(localStorageKey, data);
  } catch {
    throw new Error("Failed to save data to session storage. Please contact support.");
  }

  // Transfer user right over.
  await Router.push(route + "?handoff", route);
};

/**
 * Sanitizes a polygon name for use during handoff
 */
export const sanitizePolygonNameForHandoff = (name: string): string => {
  return name
    .replace(/,/g, "-") // Replace commas (column delimiter)
    .trim();
};
